import React, { useState } from 'react';
import { backendApi } from '../api/backendApi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Award, Calendar, DollarSign, Edit, Trash2 } from 'lucide-react';
import { Button } from '../Components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../Components/ui/card';
import { Input } from '../Components/ui/input';
import { Badge } from '../Components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../Components/ui/dialog';
import { Label } from '../Components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../Components/ui/select';

const format = (date, formatStr) => {
  const d = new Date(date);
  if (formatStr === 'MMM dd, yyyy') {
    return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }
  return d.toLocaleDateString();
};

export default function Placements() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPlacement, setEditingPlacement] = useState(null);
  const queryClient = useQueryClient();

  const { data: placements = [] } = useQuery({
    queryKey: ['placements'],
    queryFn: () => backendApi.entities.Placement.list('-created_date', 100),
    initialData: []
  });

  const createMutation = useMutation({
    mutationFn: (data) => backendApi.entities.Placement.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['placements']);
      setIsDialogOpen(false);
      setEditingPlacement(null);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => backendApi.entities.Placement.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['placements']);
      setIsDialogOpen(false);
      setEditingPlacement(null);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => backendApi.entities.Placement.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['placements']);
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    
    if (editingPlacement) {
      updateMutation.mutate({ id: editingPlacement.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const filteredPlacements = placements.filter(placement => {
    return placement.applicant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           placement.job_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           placement.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const statusColors = {
    'Offer Accepted': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'Joined': 'bg-green-100 text-green-800 border-green-300',
    'Active': 'bg-blue-100 text-blue-800 border-blue-300',
    'Completed': 'bg-gray-100 text-gray-800 border-gray-300'
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-3 rounded-lg">
              <Award className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Placements</h1>
              <p className="text-sm text-gray-500">Track successful placements</p>
            </div>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => setEditingPlacement(null)}
                className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-md"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Placement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingPlacement ? 'Edit Placement' : 'Add New Placement'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Applicant ID *</Label>
                    <Input name="applicant_id" defaultValue={editingPlacement?.applicant_id} required />
                  </div>
                  <div>
                    <Label>Job ID *</Label>
                    <Input name="job_id" defaultValue={editingPlacement?.job_id} required />
                  </div>
                  <div>
                    <Label>Applicant Name</Label>
                    <Input name="applicant_name" defaultValue={editingPlacement?.applicant_name} />
                  </div>
                  <div>
                    <Label>Job Title</Label>
                    <Input name="job_title" defaultValue={editingPlacement?.job_title} />
                  </div>
                  <div>
                    <Label>Client Name</Label>
                    <Input name="client_name" defaultValue={editingPlacement?.client_name} />
                  </div>
                  <div>
                    <Label>Start Date</Label>
                    <Input type="date" name="start_date" defaultValue={editingPlacement?.start_date} />
                  </div>
                  <div>
                    <Label>Salary</Label>
                    <Input type="number" name="salary" defaultValue={editingPlacement?.salary} />
                  </div>
                  <div>
                    <Label>Placement Fee</Label>
                    <Input type="number" name="placement_fee" defaultValue={editingPlacement?.placement_fee} />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select name="status" defaultValue={editingPlacement?.status || 'Offer Accepted'}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Offer Accepted">Offer Accepted</SelectItem>
                        <SelectItem value="Joined">Joined</SelectItem>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingPlacement ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10" />
        <Input
          placeholder="Search placements..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-12 bg-white shadow-sm border-gray-200"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPlacements.map((placement) => (
          <Card key={placement.id} className="hover:shadow-lg transition-all duration-300 border border-gray-200">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg mb-1">{placement.applicant_name}</CardTitle>
                  <div className="text-sm text-gray-600 mb-2">{placement.job_title}</div>
                  <Badge className={`${statusColors[placement.status]} border`}>
                    {placement.status}
                  </Badge>
                </div>
                <div className="flex space-x-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditingPlacement(placement);
                      setIsDialogOpen(true);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (window.confirm('Are you sure?')) {
                        deleteMutation.mutate(placement.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {placement.client_name && (
                <div className="text-sm font-medium text-emerald-600">
                  {placement.client_name}
                </div>
              )}
              {placement.start_date && (
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="h-4 w-4 mr-2" />
                  Starts: {format(new Date(placement.start_date), 'MMM dd, yyyy')}
                </div>
              )}
              {placement.salary && (
                <div className="flex items-center text-sm text-gray-600">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Salary: ${placement.salary.toLocaleString()}
                </div>
              )}
              {placement.placement_fee && (
                <div className="text-sm font-semibold text-green-600">
                  Fee: ${placement.placement_fee.toLocaleString()}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredPlacements.length === 0 && (
        <Card className="shadow-sm">
          <CardContent className="text-center py-12">
            <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No placements recorded</h3>
            <p className="text-gray-500">Add your first placement to track success</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}